import { Utensils, CreditCard, ArrowUpCircle, Clock, CoinsIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { formatParamDate } from '../utils/format-date-str';

const ctaStyle = {
  minWidth: '160px',
  lineHeight: '40px',
  textAlign: 'center',
  background: 'black',
  color: 'white',
  display: 'inline-block',
  fontSize: '14px',
  borderRadius: '4px',
};

const genSabreApiEp = ({
  sabreId,
  checkIn,
  nights = 1,
  numOfPeople = '2',
}: {
  sabreId: number;
  checkIn: string | Date;
  nights?: number;
  numOfPeople?: string;
}) => {
  try {
    const _checkIn = formatParamDate(checkIn);
    return `https://sabre-nodejs-9tia3.ondigitalocean.app/public/hotel/sabre/${sabreId}/select-rooms-price/?check_in=${_checkIn}&nights=${nights}&number_of_people=${numOfPeople}`;
  } catch (err) {
    throw new Error('cannot generate Sabre API Endpoint. Invalid checkIn value is given.');
  }
};

type SabreRoom = {
  price: number;
  roomCode: string;
  roomName: string;
  roomDescription: string;
  cancelDeadLine: string;
};

type SaberRoomWithHotelName = {
  hotelName: string;
  price: number;
  roomCode: string;
  roomName: string;
  roomDescription: string;
  cancelDeadLine: string;
};

type SabreResponseBody = {
  propertyNameKor: string;
  propertyNameEng: string;
  destinationKor: string;
  destinationEng: string;
  cityKor: string;
  cityEng: string;
  paragonId: number;
  roomDescriptions: SabreRoom[];
};

type SelectHotelProductItemProps = {
  // 외부에서 주입 받는 프로퍼티들.
  sabreId: number; // 세이버 아이디
  checkIn: Date | string; // 희망 체크인 날짜
  nights?: number; // 희망 투숙 박 수
  numOfPeople?: string; // 희망 투숙 인원 정보 스트링

  // 사실상 하드코딩이지만 외부로부터의 주입도 가능한 값
  widgetTitle?: string; // 위젯 최상단 타이틀
  consultingCaption?: string; // 상담 유도 문구, 상담 버튼에 들어갈 캡션. 예: "채팅 상담"
  utensilsCaption?: string; // 조식 혜택 문구
  creditCardCaption?: string; // 식음료 크레딧 혜택 문구
  arrowUpCircleCaption?: string; // 객실 업그레이드 혜택 문구
  clockCaption?: string; // 얼리 체크인 & 레이트 체크아웃 혜택 문구
  coinsCaption?: string; // 멤버십 포인트 혜택 문구
  affCution?: string; // 카드 제휴 주의사항 문구
  caution?: string; // 주의사항 문구
  roomPricingCaption?: string; // 객실 요금 문구
  nightsPricingCaption?: string; // ~박 예상결제가 문구
};

export function SelectHotelProductItem({
  // 외부로부터 주입 받는 값
  sabreId, // 세이버 아이디
  checkIn, // 희망 체크인 날짜
  nights = 1, // 희망 투숙 박 수
  numOfPeople = '2', // 희망 투숙 인원 정보 스트링

  // 사실상 기본값으로 하드코딩이지만 외부로부터의 주입도 가능한 값
  //// 위젯 최상단 타이틀
  widgetTitle = 'Luxury Concierge - 후불 현장 결제',
  //// 상담 유도 문구, 상담 버튼에 들어갈 캡션. 예: "채팅 상담"
  consultingCaption = '1:1 채팅문의',
  //// 조식 혜택 문구
  utensilsCaption = '2인 조식 포함',
  //// 식음료 크레딧 혜택 문구
  creditCardCaption = '$100 식음료 크레딧 제공',
  //// 객실 업그레이드 혜택 문구
  arrowUpCircleCaption = '객실 무료 업그레이드 (현장 가능시)',
  //// 얼리 체크인 & 레이트 체크아웃 혜택 문구
  clockCaption = '얼리체크인 & 레이트 체크아웃 (현장 가능시)',
  //// 멤버십 포인트 혜택 문구
  coinsCaption = '호텔 멤버십 포인트 적립 (사전 회원가입 후 요청시)',
  //// 카드 제휴 주의사항 문구
  affCution = '카드제휴 할인 및 혜택 적용이 되지 않는 요금제 입니다.',
  //// 주의사항 문구
  caution = '현장 결제시 환율에 따라 최종 원화 결제 금액이 변동될 수 있습니다.',
  //// 객실 요금 문구
  roomPricingCaption = '일반요금',
  //// ~박 예상결제가 문구
  nightsPricingCaption = '1박 평균가',
}: SelectHotelProductItemProps) {
  const [resData, setResData] = useState<SabreResponseBody | undefined | null>();
  const [isLoading, setIsLoading] = useState(false);
  const [firstRoom, setFirstRoom] = useState<
    SaberRoomWithHotelName | string | null | undefined
  >();

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const url = genSabreApiEp({ sabreId, checkIn, nights, numOfPeople });

        const res = await fetch(url, {
          method: 'GET',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          mode: 'cors', // CORS 명시적 설정
        });

        if (!res.ok) {
          throw new Error(`HTTP error! status: ${res.status}`);
        }

        const text = await res.text();
        const data = JSON.parse(text);
        setResData(data);
      } catch (err) {
        console.error('Fetch error:', err);
        if (err instanceof TypeError && err.message.includes('Failed to fetch')) {
          console.error('CORS error or network issue. Check browser console for details.');
        }
        setResData(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [checkIn, nights, numOfPeople, sabreId]);

  useEffect(() => {
    try {
      if (resData === null) throw new Error('no resData');
      const roomDescriptions = resData?.roomDescriptions;
      if (!roomDescriptions?.length || roomDescriptions?.length <= 0)
        throw new Error('no roomDescription');
      const sortedRooms = roomDescriptions?.sort((a, b) => (a?.price || 0) - (b.price || 0));
      if (!sortedRooms?.length || sortedRooms?.length <= 0) {
        throw new Error('failed to sort');
      }
      const firstRoom = sortedRooms?.[0] || null;
      if (firstRoom === null) {
        throw new Error('no firstRoom');
      }
      if (
        (typeof resData?.propertyNameKor !== 'string' &&
          typeof resData?.propertyNameEng !== 'string') ||
        typeof firstRoom.price !== 'number' ||
        typeof firstRoom.roomDescription !== 'string' ||
        typeof firstRoom.cancelDeadLine !== 'string'
      )
        throw new Error('invalid room description data');
      setFirstRoom({
        ...firstRoom,
        hotelName: firstRoom?.roomCode || resData.propertyNameKor || resData.propertyNameEng,
        cancelDeadLine: firstRoom.cancelDeadLine.replace(/^(\d{4})(\d{2})(\d{2})$/, '$1-$2-$3'),
      });
    } catch (err) {
      setFirstRoom(`${consultingCaption} 필요`);
    }
  }, [resData]);

  return (
    <div
      className="cx-dm-select-overflow-hidden cx-dm-select-bg-white cx-dm-select-shadow-md md:cx-dm-select-shadow-none cx-dm-select-rounded-xl"
      style={{
        border: '1px solid #e5398f',
        marginTop: '30px',
      }}
    >
      <header className="cx-dm-select-bg-[#e5398f] cx-dm-select-text-white cx-dm-select-p-3 cx-dm-select-text-center sm:cx-dm-select-text-left cx-dm-select-font-medium">
        {`${widgetTitle}`}
      </header>
      <div className="cx-dm-select-grid cx-dm-select-grid-cols-1 lg:cx-dm-select-grid-cols-5 lg:cx-dm-select-gap-0 cx-dm-select-items-start cx-dm-select-justify-stretch">
        {/* Left Column: Image + Details (60%) */}
        <div className="lg:cx-dm-select-col-span-3 ">
          <div className="cx-dm-select-grid cx-dm-select-grid-cols-1 sm:cx-dm-select-grid-cols-3 md:cx-dm-select-gap-6 cx-dm-select-items-start">
            {/* Details Section */}
            <div className="cx-dm-select-col-span-3 cx-dm-select-flex cx-dm-select-flex-col cx-dm-select-gap-3 cx-dm-select-p-5 lg:cx-dm-select-p-[30px]">
              {
                (isLoading || !firstRoom) ? (
                  <div className="cx-dm-select-w-72 cx-dm-select-max-w-full cx-dm-select-h-7 cx-dm-select-animate-pulse cx-dm-select-bg-gray-200 cx-dm-select-rounded-sm" />
                ) : typeof firstRoom !== 'string' && (
                  <span className="cx-dm-select-font-bold cx-dm-select-text-xl" style={{
                    lineHeight: '28px',
                    minHeight: '28px',
                    fontSize: '18px',
                    fontWeight: 600,
                  }}>{firstRoom.hotelName}</span>
                ) 
              }
              <div className="cx-dm-select-pl-0 md:cx-dm-select-pl-6">
                <ul className="cx-dm-select-flex cx-dm-select-flex-col cx-dm-select-gap-1">
                  {[
                    {
                      icon: (
                        <Utensils className="cx-dm-select-w-4 cx-dm-select-h-4 cx-dm-select-text-gray-500 cx-dm-select-px-0 cx-dm-select-py-0" />
                      ),
                      benefit: `${utensilsCaption}`,
                    },
                    {
                      icon: (
                        <CreditCard className="cx-dm-select-w-4 cx-dm-select-h-4 cx-dm-select-text-gray-500" />
                      ),
                      benefit: `${creditCardCaption}`,
                    },
                    {
                      icon: (
                        <ArrowUpCircle className="cx-dm-select-w-4 cx-dm-select-h-4 cx-dm-select-text-gray-500" />
                      ),
                      benefit: `${arrowUpCircleCaption}`,
                    },
                    {
                      icon: (
                        <Clock className="cx-dm-select-w-4 cx-dm-select-h-4 cx-dm-select-text-gray-500" />
                      ),
                      benefit: `${clockCaption}`,
                    },
                    {
                      icon: (
                        <CoinsIcon className="cx-dm-select-w-4 cx-dm-select-h-4 cx-dm-select-text-gray-500" />
                      ),
                      benefit: `${coinsCaption}`,
                    },
                  ].map((item, index) => (
                    <li
                      style={{
                        marginTop: 0,
                      }}
                      key={index}
                      className="cx-dm-select-flex cx-dm-select-items-start cx-dm-select-gap-2 cx-dm-select-text-sm cx-dm-select-text-gray-600"
                    >
                      {typeof item.icon === 'string' ? (
                        <img
                          src={item.icon || '/placeholder.svg'}
                          alt=""
                          width={16}
                          height={16}
                        />
                      ) : (
                        item.icon
                      )}
                      <span>{item.benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <p className="cx-dm-select-text-xs cx-dm-select-text-gray-400 cx-dm-select-text-left cx-dm-select-flex cx-dm-select-gap-[4px] cx-dm-select-flex-nowrap cx-dm-select-pl-0 md:cx-dm-select-pl-6">
                <span className="cx-dm-select-shrink-0">※</span>
                <span className="cx-dm-select-shrink cx-dm-select-text-wrap">{`${affCution}`}</span>
              </p>
            </div>
          </div>
        </div>

        {/* Right Column: Pricing with divider (40%) */}
        <div className="lg:cx-dm-select-col-span-2 cx-dm-select-p-5 lg:cx-dm-select-p-[30px] lg:cx-dm-select-border-l lg:cx-dm-select-border-dashed lg:cx-dm-select-border-gray-300 cx-dm-select-flex cx-dm-select-flex-col cx-dm-select-justify-between cx-dm-select-h-full cx-dm-select-border-t cx-dm-select-border-dashed lg:cx-dm-select-border-t-0">
          <div className="cx-dm-select-flex cx-dm-select-flex-row sm:cx-dm-select-justify-between sm:cx-dm-select-items-start cx-dm-select-mb-4 cx-dm-select-w-full cx-dm-select-gap-2 cx-dm-select-justify-between cx-dm-select-items-start">
            <div className="cx-dm-select-text-sm cx-dm-select-text-gray-500 cx-dm-select-flex cx-dm-select-items-center cx-dm-select-h-full">
              <span>{`${roomPricingCaption}`}</span>
            </div>
            <div className="cx-dm-select-text-left sm:cx-dm-select-text-right">
              <div className="cx-dm-select-text-gray-500 cx-dm-select-text-right">
                <span>{`${nightsPricingCaption}`}</span>
              </div>
              <div className="cx-dm-select-font-bold cx-dm-select-text-gray-800 cx-dm-select-text-2xl">
                {isLoading || !firstRoom ? (
                  <div className="cx-dm-select-flex cx-dm-select-flex-col cx-dm-select-items-end cx-dm-select-gap-1">
                    <div className="cx-dm-select-w-40 cx-dm-select-h-8 cx-dm-select-animate-pulse cx-dm-select-bg-gray-200 cx-dm-select-rounded-sm" />
                    <div className="cx-dm-select-w-20 cx-dm-select-h-5 cx-dm-select-animate-pulse cx-dm-select-bg-gray-200 cx-dm-select-rounded-sm" />
                  </div>
                ) : typeof firstRoom === 'string' ? (
                  firstRoom
                ) : (
                  <div className="cx-dm-select-flex cx-dm-select-flex-col cx-dm-select-gap-1">
                    <span style={{ fontWeight: 800, fontSize: '20px', textAlign: 'right' }}>{`${(Math.round(firstRoom.price / nights)).toLocaleString('ko-KR')}원`}</span>
                    <span className="cx-dm-select-text-sm cx-dm-select-font-normal cx-dm-select-text-gray-500" style={{ textAlign: 'right' }}>{nights}박 {firstRoom.price.toLocaleString('ko-KR')}원</span>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="cx-dm-select-w-full cx-dm-select-text-right cx-dm-select-space-y-4 cx-dm-select-mt-auto">
            {/* api 로부터 가져오는 룸 디스크립션을 사용해야 할 경우 아래 주석을 해제 할것 */}
            {/* <div dangerouslySetInnerHTML={{ __html: typeof resData !== 'string' && resData?.roomDescriptions?.length &&
                  `${resData?.roomDescriptions
                    ?.sort((a, b) => {
                      return (a?.price || 0) - (b.price || 0);
                    })[0].roomDescription.replace(/<html.+html>/, '') }` || '' }} /> */}
            {/* api 로부터 가져오는 룸 디스크립션을 사용해야 할 경우 아래 치환 섹션을 주석 처리 할것 */}
            <p className="cx-dm-select-text-xs cx-dm-select-text-red-500 cx-dm-select-text-left cx-dm-select-flex cx-dm-select-gap-[4px] cx-dm-select-flex-nowrap md:cx-dm-select-justify-end">
              <span className="cx-dm-select-shrink-0">※</span>
              <span className="cx-dm-select-shrink cx-dm-select-text-wrap">{`${caution}`}</span>
            </p>
            <button
              className="cx-dm-select-w-full md:cx-dm-select-w-fit"
              style={{
                minWidth: '160px',
                lineHeight: '40px',
                textAlign: 'center',
                background: 'black',
                color: 'white',
                display: 'inline-block',
                fontSize: '14px',
                borderRadius: '4px',
              }}
              onClick={() => {
                if (typeof window?.zE !== 'function') {
                  window.location.href = 'https://pf.kakao.com/_cxmxgNG/chat';
                  return;
                }
                window?.zE('messenger', 'open');
              }}
            >
              {`${consultingCaption}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
